'use client';

import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import AutoScroll from 'embla-carousel-auto-scroll';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import iconAssets from '@/assets/icons';

interface CarouselItem {
  key: string;
  src: string;
  label: string;
}

interface IconCarouselProps {
  items?: CarouselItem[];
  autoScroll?: boolean;
  autoScrollSpeed?: number;
  showControls?: boolean;
  className?: string;
}

const DEFAULT_ITEMS: CarouselItem[] = [
  { key: 'natural100', src: iconAssets.natural100, label: '100% Natural Extract' },
  { key: 'antiBactaria', src: iconAssets.antiBactaria, label: 'Anti Bacterial' },
  { key: 'authentic', src: iconAssets.authentic, label: 'Authentic Formula' },
  { key: 'madeInIndia', src: iconAssets.madeInIndia, label: 'Made In India' },
  { key: 'nonToxic', src: iconAssets.nonToxic, label: 'Non Toxic' },
  { key: 'bioDegradable', src: iconAssets.bioDegradable, label: 'Biodegradable' },
  { key: 'chlorineFree', src: iconAssets.chlorineFree, label: 'Chlorine Free' },
  { key: 'ecoFriendly', src: iconAssets.ecoFriendly, label: 'Eco Friendly' },
  { key: 'killGerms', src: iconAssets.killGerms, label: 'Kills Germs' },
  { key: 'noMushroom', src: iconAssets.noMushroom, label: 'Do not use on mushrooms' },
  { key: 'doNotUseOnCut', src: iconAssets.doNotUseOnCut, label: 'Do Not Use On Cut and Chopped Produce' },
  { key: 'clinicallyProven', src: iconAssets.clinicallyProven, label: 'Clinically Proven' },
  { key: 'noAddedSyntheticColor', src: iconAssets.noAddedSyntheticColor, label: 'No Synthetic Color' },
  { key: 'noHarshChemicals', src: iconAssets.noHarshChemicals, label: 'No Harsh Chemicals' },
  { key: 'planetFriendly', src: iconAssets.planetFriendly, label: 'Planet Friendly' },
  { key: 'skinFriendly', src: iconAssets.skinFriendly, label: 'Skin Friendly' },
  { key: 'soapFree', src: iconAssets.soapFree, label: 'No Added Soap' },
  // { key: 'gmpCertified', src: iconAssets.gmpCertified, label: 'GMP Certified' },
  // { key: 'iaf', src: iconAssets.iaf, label: 'IAF Certified' },
  // { key: 'iso2015', src: iconAssets.iso2015, label: 'ISO 2015 Certified' }
];

export default function IconCarousel({
  items = DEFAULT_ITEMS,
  autoScroll = true,
  autoScrollSpeed = 0.9,
  showControls = true,
  className = ''
}: IconCarouselProps) {
  const plugins = React.useMemo(() => {
    if (!autoScroll || items.length < 2) {
      return [];
    }

    return [
      AutoScroll({
        speed: autoScrollSpeed,
        startDelay: 0,
        direction: 'forward',
        defaultInteraction: false
      })
    ];
  }, [autoScroll, autoScrollSpeed, items.length]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: true,
      loop: items.length > 1
    },
    plugins
  );

  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const playAutoScroll = React.useCallback(() => {
    if (!autoScroll) {
      return;
    }

    emblaApi?.plugins().autoScroll?.play();
  }, [autoScroll, emblaApi]);

  const stopAutoScroll = React.useCallback(() => {
    emblaApi?.plugins().autoScroll?.stop();
  }, [emblaApi]);

  const updateControls = React.useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setCanScrollPrev(emblaApi.canGoToPrev());
    setCanScrollNext(emblaApi.canGoToNext());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    updateControls();
    playAutoScroll();
    emblaApi.on('select', updateControls);
    emblaApi.on('reinit', updateControls);
    emblaApi.on('pointerdown', stopAutoScroll);
    emblaApi.on('pointerup', playAutoScroll);

    return () => {
      emblaApi.off('select', updateControls);
      emblaApi.off('reinit', updateControls);
      emblaApi.off('pointerdown', stopAutoScroll);
      emblaApi.off('pointerup', playAutoScroll);
      stopAutoScroll();
    };
  }, [emblaApi, playAutoScroll, stopAutoScroll, updateControls]);

  const goToPrev = React.useCallback(() => {
    emblaApi?.goToPrev();
    playAutoScroll();
  }, [emblaApi, playAutoScroll]);

  const goToNext = React.useCallback(() => {
    emblaApi?.goToNext();
    playAutoScroll();
  }, [emblaApi, playAutoScroll]);

  return (
    <div
      className={className}
      onMouseEnter={autoScroll ? stopAutoScroll : undefined}
      onMouseLeave={autoScroll ? playAutoScroll : undefined}
      onFocusCapture={autoScroll ? stopAutoScroll : undefined}
      onBlurCapture={autoScroll ? playAutoScroll : undefined}>
      <div className="embla" aria-label="Dr. Detoxer certifications and care icons">
        <div className="embla__viewport overflow-hidden rounded-3xl" ref={emblaRef}>
          <div className="embla__container -ml-4 flex touch-pan-y touch-pinch-zoom">
            {items.map((item) => (
              <div
                key={item.key}
                className="embla__slide min-w-0 flex-[0_0_42%] pl-4 sm:flex-[0_0_30%] md:flex-[0_0_18%] lg:flex-[0_0_22%] xl:flex-[0_0_18%]">
                <div className="flex min-h-[152px] flex-col items-center justify-center gap-1 sm:gap-3 rounded-[1.25rem] px-4 py-5 text-center">
                  <img
                    src={item.src}
                    alt={item.label}
                    className="h-16 w-16 object-contain sm:h-20 sm:w-20"
                  />
                  <span className="text-xs font-semibold leading-snug text-primary sm:text-sm">
                    {item.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showControls && (
          <div className="mt-4 flex items-center justify-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={goToPrev}
              disabled={!canScrollPrev}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-foreground transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Show previous icons">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={goToNext}
              disabled={!canScrollNext}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-foreground transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Show next icons">
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
